import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Link as LinkIcon, 
  Copy, 
  Check, 
  X, 
  Trash2, 
  Clock,
  Mail,
  Shield,
  UserCheck,
  UserX,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamMembers() {
  const { user, isAdmin } = useAuth();
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [activeTab, setActiveTab] = useState('members');

  const menuColor = user?.menu_color || '#064E3B';

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      const [invitesRes, membersRes] = await Promise.all([
        axios.get(`${API}/invites`),
        axios.get(`${API}/team-members`)
      ]);
      setInvites(invitesRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/invites`, {
        email: inviteEmail || null
      });
      setInvites([response.data, ...invites]);
      setInviteEmail('');
      toast.success('Invite link created!');
    } catch (error) {
      toast.error('Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const deleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/invites/${inviteId}`);
      setInvites(invites.filter(i => i.id !== inviteId));
      toast.success('Invite deleted');
    } catch (error) {
      toast.error('Failed to delete invite');
    }
  };

  const approveMember = async (memberId) => {
    try {
      await axios.put(`${API}/team-members/${memberId}/approve`);
      setMembers(members.map(m => 
        m.id === memberId ? { ...m, status: 'approved' } : m
      ));
      toast.success('Member approved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve member');
    }
  };

  const rejectMember = async (memberId) => {
    try {
      await axios.put(`${API}/team-members/${memberId}/reject`);
      setMembers(members.map(m => 
        m.id === memberId ? { ...m, status: 'rejected' } : m
      ));
      toast.success('Member rejected');
    } catch (error) {
      toast.error('Failed to reject member');
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await axios.delete(`${API}/team-members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const copyInviteLink = async (token) => {
    const link = `${window.location.origin}/join/${token}`;
    
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        // Fallback for older browsers or restricted contexts
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedId(token);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      // Final fallback - show the link in a prompt
      window.prompt('Copy this invite link:', link);
    }
  };

  const getStatusBadge = (status, emailVerified) => {
    if (status === 'pending_verification' || !emailVerified) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Mail className="w-3 h-3" />
          Awaiting Email Verification
        </span>
      );
    }
    if (status === 'pending_approval') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Clock className="w-3 h-3" />
          Pending Approval
        </span>
      );
    }
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <UserCheck className="w-3 h-3" />
          Approved
        </span>
      );
    }
    if (status === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <UserX className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    return null;
  };

  const pendingCount = members.filter(m => m.status === 'pending_approval').length;

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center mb-6">
            <Shield className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Access Denied</h2>
          <p className="text-gray-500">Only team administrators can access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="team-members-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
              Team Members
            </h1>
            <p className="text-gray-500 mt-1">Manage invites and team member access</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'members'
                ? 'border-current text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === 'members' ? { borderColor: menuColor, color: menuColor } : {}}
            data-testid="members-tab"
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Members
              {pendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {pendingCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'invites'
                ? 'border-current text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === 'invites' ? { borderColor: menuColor, color: menuColor } : {}}
            data-testid="invites-tab"
          >
            <div className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Invite Links
            </div>
          </button>
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : members.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
                  <p className="text-gray-500 mb-4">Create an invite link to add players to your team</p>
                  <Button
                    onClick={() => setActiveTab('invites')}
                    style={{ backgroundColor: menuColor }}
                    className="text-white"
                    data-testid="create-first-invite-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Invite
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {members.map((member) => (
                  <Card key={member.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                            style={{ backgroundColor: menuColor }}
                          >
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{member.name}</h3>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 ml-13 sm:ml-0">
                          {getStatusBadge(member.status, member.email_verified)}
                          <div className="flex gap-2">
                            {member.status === 'pending_approval' && member.email_verified && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => approveMember(member.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`approve-${member.id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => rejectMember(member.id)}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`reject-${member.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeMember(member.id)}
                              className="text-gray-500 hover:text-red-600"
                              data-testid={`remove-${member.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invites Tab */}
        {activeTab === 'invites' && (
          <div className="space-y-6">
            {/* Create Invite */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Create New Invite
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder="Email (optional)"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="flex-1"
                    data-testid="invite-email-input"
                  />
                  <Button
                    onClick={createInvite}
                    disabled={creating}
                    style={{ backgroundColor: menuColor }}
                    className="text-white whitespace-nowrap"
                    data-testid="create-invite-btn"
                  >
                    {creating ? 'Creating...' : 'Generate Invite Link'}
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Leave email blank to create a generic invite link that anyone can use.
                </p>
              </CardContent>
            </Card>

            {/* Invite List */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Active Invites</h3>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : invites.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    No invite links created yet
                  </CardContent>
                </Card>
              ) : (
                invites.map((invite) => {
                  const isExpired = new Date(invite.expires_at) < new Date();
                  return (
                    <Card key={invite.id} className={invite.used || isExpired ? 'opacity-60' : ''}>
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {invite.email ? (
                                <span className="text-sm font-medium text-gray-900">{invite.email}</span>
                              ) : (
                                <span className="text-sm text-gray-500">Generic invite link</span>
                              )}
                              {invite.used && (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Used</span>
                              )}
                              {isExpired && !invite.used && (
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Expired</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                              {window.location.origin}/join/{invite.token}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {!invite.used && !isExpired && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyInviteLink(invite.token)}
                                data-testid={`copy-invite-${invite.id}`}
                              >
                                {copiedId === invite.token ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteInvite(invite.id)}
                              className="text-gray-500 hover:text-red-600"
                              data-testid={`delete-invite-${invite.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
